import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { catchError, filter, map, mergeMap, of, tap, withLatestFrom } from "rxjs";
import { ISchool } from "../../models/academic";
import { IStudent } from "../../models/user";
import { GlobalDataService } from "../../services/faker/global-data.service";
import { NotificationService } from "../../services/notifications/notifications.service";
import { SchoolActions } from "../schools/school.actions";
import { StudentActions } from "../students/student.actions";
import { NotificationActions } from "./notification.actions";
import { selectStudentNotificationSettings } from "./notification.selectors";

@Injectable()
export class NotificationEffects {
    constructor(
        private actions$: Actions,
        private globalDataService: GlobalDataService,
        private notificationService: NotificationService,
        private store: Store
    ) { }

    loadNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationActions.loadNotifications),
            mergeMap(() => {
                const data = this.globalDataService.getNotifications();
                if (data && data.length > 0) {
                    return of(data).pipe(
                        map(notifications => NotificationActions.loadNotificationsSuccess({ notifications })),
                        catchError(error => of(NotificationActions.loadNotificationsFailure({ error })))
                    );
                } else {
                    return of(NotificationActions.loadNotificationsFailure({ error: 'Resources data not found' }));
                }
            })
        )
    );

    // Effect to listen for AddNotification action and trigger notification popup
    showNotification$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(NotificationActions.addNotification),
                withLatestFrom(this.store.pipe(select(selectStudentNotificationSettings))),
                filter(([action, studentSettings]) => {
                    const notification = action.notification;
                    if (!notification.relatedIds) {
                        return false;
                    }
                    const relatedStudent = notification.relatedIds.find(rel => rel.name === 'student');
                    const relatedStudentId = relatedStudent?.identifier;

                    // If no related student or no settings found, do not show the notification
                    if (!relatedStudentId || !studentSettings[relatedStudentId]) {
                        return false;
                    }

                    const studentSetting = studentSettings[relatedStudentId];

                    // Check the notification type against the student's settings
                    switch (notification.icon) {
                        case 'report':
                            return studentSetting.reportCards;
                        case 'checklist-apple':
                            return studentSetting.homework ?? true;
                        case 'group-achievement':
                            return studentSetting.marks;
                        case 'schedule':
                            return studentSetting.timetables;
                        case 'podium':
                            return studentSetting.matchResults ?? true;
                        case 'bus':
                            return studentSetting.attendance ?? true;
                        default:
                            return true; // Show notification if it doesn't match any specific type
                    }
                }),
                tap(([action, _]) => {
                    this.notificationService.showNotification(action.notification);
                })
            ),
        { dispatch: false } // Since this effect doesn't dispatch any actions, set dispatch to false
    );

    //REMOVE LATER WHEN WE GET THESE VALUES FROM SERVER

    //Effect that triggers Initializing of the Student Notification Settings when the students are loaded
    initializeStudentNotificationSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StudentActions.loadStudentsSuccess),
            map(action => {
                const studentIds = action.students.map((student: IStudent) => ({ id: student.id }));
                return NotificationActions.initialiseStudentSettings({ students: studentIds });
            })
        )
    );
    //Effect that triggers Initializing of the School Notification Settings when the schools are loaded
    initializeSchoolNotificationSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SchoolActions.loadSchoolsSuccess),
            map(action => {
                const schoolIds = action.schools.map((school: ISchool) => ({ id: school.id }));
                return NotificationActions.initialiseSchoolSettings({ schools: schoolIds });
            })
        )
    );
}